import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, Phone } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminApi, type AdminApplicationDetail } from '@/services/adminApi';

/**
 * AdminApplicationDetailPage — single application deep-dive for admins.
 *
 * Shows resolved company + position labels, full candidate snapshot,
 * the application state with optional `lastFailureReason`, AI scores
 * (when present), and the raw doc dump for fields not yet modeled.
 *
 * Read-only in MVP. Future write actions ("re-process", "anonymize",
 * "force resolve") will land in BO-02.
 */
export default function AdminApplicationDetailPage() {
  const { companyId = '', posId = '', appId = '' } =
    useParams<{ companyId: string; posId: string; appId: string }>();
  const [data, setData] = useState<AdminApplicationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await adminApi.applications.detail(companyId, posId, appId);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, posId, appId]);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/admin/applications"><ArrowLeft className="h-4 w-4" /> Back to applications</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-md bg-primary/10 flex items-center justify-center text-xl font-semibold text-primary">
          {(data.candidate.firstName || data.candidate.email || '?')[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold">{data.candidateName || '(unknown candidate)'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <Link to={`/admin/customers/${data.companyId}`} className="hover:underline">
              {data.companyName || data.companyId}
            </Link>
            {' · '}
            <span>{data.positionTitle || data.jobPositionId}</span>
            {' · '}
            <span>{data.channel || 'no channel'}</span>
          </p>
          <p className="text-xs text-muted-foreground font-mono mt-1">{data.applicationId}</p>
        </div>
        <StatePill state={data.state} />
      </div>

      {/* Candidate identity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Candidate
            </h2>
            <Row
              k="Email"
              v={data.candidate.email ? (
                <a href={`mailto:${data.candidate.email}`} className="hover:underline inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {data.candidate.email}
                </a>
              ) : '—'}
            />
            <Row
              k="Phone"
              v={data.candidate.phone ? (
                <a href={`tel:${data.candidate.phone}`} className="hover:underline inline-flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" />
                  {data.candidate.phone}
                </a>
              ) : '—'}
            />
            {data.candidate.cvUrl && (
              <Row
                k="CV"
                v={
                  <a href={data.candidate.cvUrl} target="_blank" rel="noopener noreferrer"
                     className="hover:underline inline-flex items-center gap-1.5">
                    Open CV <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                }
              />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              State
            </h2>
            <Row k="Current" v={data.state} />
            <Row
              k="Failure"
              v={data.lastFailureReason ? (
                <span className={`text-xs px-2 py-0.5 rounded-md ${
                  data.lastFailureReason === 'billing_limit_reached'
                    ? 'bg-amber-100 text-amber-900'
                    : 'bg-destructive/10 text-destructive'
                }`}>
                  {data.lastFailureReason}
                </span>
              ) : '—'}
            />
            <Row k="Applied" v={data.appliedAt ? new Date(data.appliedAt).toLocaleString('en-GB') : '—'} />
            <Row k="Updated" v={data.updatedAt ? new Date(data.updatedAt).toLocaleString('en-GB') : '—'} />
          </CardContent>
        </Card>
      </div>

      {/* AI scores */}
      {data.scores && Object.keys(data.scores).length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              AI scores
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(data.scores).map(([k, v]) => (
                <div key={k} className="space-y-0.5">
                  <p className="text-xs text-muted-foreground capitalize">{k}</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {typeof v === 'number' ? v.toFixed(1) : String(v)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill-down */}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/customers/${data.companyId}`}>Open customer</Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to={`/admin/applications?companyId=${data.companyId}`}>
            All applications for this company
          </Link>
        </Button>
        {data.jobPostingId && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/admin/customers/${data.companyId}/positions/${data.jobPositionId}/postings/${data.jobPostingId}`}>
              View source posting
            </Link>
          </Button>
        )}
      </div>

      {/* Raw JSON */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50">
            <h2 className="text-sm font-semibold">Raw application document</h2>
          </div>
          <pre className="p-5 text-xs overflow-x-auto bg-muted/30 max-h-[400px]">
            {JSON.stringify(data.raw, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-24 shrink-0">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}

function StatePill({ state }: { state: string }) {
  const errorish = state === 'PIPELINE_ERROR' || state === 'REJECTED' || state === 'WITHDRAWN';
  const successish = state === 'HIRED' || state === 'OFFER';
  const cls = errorish
    ? 'bg-destructive/10 text-destructive'
    : successish
      ? 'bg-success/15 text-success'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`text-xs px-2 py-1 rounded-md font-medium uppercase tracking-wide ${cls}`}>
      {state}
    </span>
  );
}
